import * as RadixSelect from '@radix-ui/react-select';
import { cn } from '../utils/cn';
import selectArrowIcon from '../assets/select_arrow.svg';
import selectActiveIcon from '../assets/select_active.svg';

const Select = ({
  options = [],
  value,
  onValueChange,
  disabled = false,
  className,
  contentClassName,
  placeholder,
  displayFormat,
  maxHeight = "300px", // Default max height to prevent off-screen issues
  maxVisibleItems, // Alternative to maxHeight - specify number of items to show
}) => {
  // Calculate height based on maxVisibleItems if provided
  const calculatedMaxHeight = maxVisibleItems
    ? `${maxVisibleItems * 37 + 8}px` // 37px per item (including padding) + 8px for container padding
    : maxHeight;
  return (
    <RadixSelect.Root value={value} onValueChange={onValueChange}>
      <RadixSelect.Trigger
        className={cn(
          "w-24 h-9 px-4 bg-white rounded-3xl border justify-between items-center inline-flex",
          "focus:outline-none",
          disabled ? "bg-[#eeeeee] border-[#dddddd]" : "border-[#dddddd] hover:border-[#111111]",
          "data-[state=open]:border-[#111111]",
          className
        )}
        disabled={disabled}
      >
        <RadixSelect.Value
          placeholder={placeholder}
          asChild
        >
          <span
            className={cn(
              "flex-1 basis-0 text-sm font-normal leading-[21px] truncate",
              disabled ? "text-[#aaaaaa]" : "text-[#111111]",
              "overflow-hidden whitespace-nowrap text-ellipsis",
            )}
          >
            {displayFormat 
              ? displayFormat(value, options.find(option => option.value === value))
              : options.find(option => option.value === value)?.label || placeholder}
          </span>
        </RadixSelect.Value>
        <RadixSelect.Icon className="ml-2">
          <img src={selectArrowIcon} alt="arrow" className='w-[18px] h-[18px]' />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          side="bottom"
          align="start"
          sideOffset={4}
          avoidCollisions={true}
          collisionPadding={8}
          className={cn(
            "bg-white rounded-md shadow-[0px_4px_12px_0px_rgba(0,0,0,0.10)] border border-[#dddddd] overflow-hidden",
            "min-w-44 w-[var(--radix-select-trigger-width)]",
            "z-50", // Ensure dropdown appears above other elements
            contentClassName
          )}
          style={{ maxHeight: calculatedMaxHeight }}
        >
          <RadixSelect.Viewport
            className={cn(
              "p-1 overflow-auto",
              // Custom scrollbar styling for webkit browsers
              "[&::-webkit-scrollbar]:w-2",
              "[&::-webkit-scrollbar-track]:bg-transparent",
              "[&::-webkit-scrollbar-thumb]:bg-gray-300",
              "[&::-webkit-scrollbar-thumb]:rounded-full",
              "[&::-webkit-scrollbar-thumb:hover]:bg-gray-400",
              // For Firefox
              "scrollbar-width-thin scrollbar-color-gray-300"
            )}
            style={{
              maxHeight: `calc(${calculatedMaxHeight} - 2px)`, // Account for border
              scrollbarWidth: 'thin',
              scrollbarColor: '#d1d5db transparent'
            }}
          >
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value + option.label}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  "px-3 py-[7.50px] rounded flex items-center",
                  "focus:outline-none cursor-pointer",
                  "data-[disabled]:text-[#aaaaaa] data-[disabled]:cursor-not-allowed",
                  "data-[highlighted]:bg-[#f4f4f4]"
                )}
              >
                <RadixSelect.ItemText asChild>
                  <span className="flex-1 mr-2 text-sm font-normal leading-[21px]">
                    {option.label}
                  </span>
                </RadixSelect.ItemText>
                <RadixSelect.ItemIndicator>
                  <img src={selectActiveIcon} alt="selected" className="w-4 h-4"/>
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
};

export default Select; 